<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, PropType, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import InitialCircle from '../InitialCircle.vue';

// #region interfaces
import { SlotHour } from '../../interfaces/slot-hour.interface';
import { TimeClockCardResponse } from '../../interfaces/time-clock-card-response.interface';
// #endregion

const { t } = useI18n();

const props = defineProps({ content: { type: Object as PropType<TimeClockCardResponse>, default: () => ({}) } });

const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const slot_date = computed(() => {
   const date = new Date(props.content.slot.date);
   return `${DAYS_OF_WEEK[date.getDay() - 1]} ${date.getDate()}/${('0' + date.getMonth()).slice(-2)}`;
});

// #region seconds performed
const slot_hours_total = computed(() => formatTimeFromSeconds(props.content.slot.seconds_planned));
const perfomed_hours_total = ref<string>(formatTimeFromSeconds(props.content.slot.seconds_performed));

function formatTimeFromSeconds(total_seconds: number) {
   const hours = Math.floor(total_seconds / 3600);
   const minutes = Math.floor((total_seconds % 3600) / 60);
   const seconds = Math.floor(total_seconds % 60);

   return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}
// #endregion

// #region interval timeclock
const time_clock_in_progress = ref<boolean>(false);
const increment_time_interval = ref<NodeJS.Timeout>();
onMounted(() => {
   if (props.content.slot.performed_hours[props.content.slot.performed_hours.length - 1]?.end_time) return;

   time_clock_in_progress.value = true;
   increment_time_interval.value = setInterval(incrementTime, 1000);
});

onBeforeUnmount(() => {
   if (increment_time_interval.value) clearInterval(increment_time_interval.value);
});

function incrementTime() {
   const [hours, minutes, seconds] = perfomed_hours_total.value.split(':').map(Number);

   let totalSeconds = hours * 3600 + minutes * 60 + seconds;
   totalSeconds++;

   const newHours = Math.floor(totalSeconds / 3600);
   const newMinutes = Math.floor((totalSeconds % 3600) / 60);
   const newSeconds = Math.floor(totalSeconds % 60);

   perfomed_hours_total.value = `${newHours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}:${newSeconds
      .toString()
      .padStart(2, '0')}`;
}
// #endregion

// #region message
function getIndexOfActiveTimeObject(slot_hours: SlotHour[]) {
   const CURRENT_TIME = new Date();

   return slot_hours.findIndex(({ start_time, end_time }: { start_time: string; end_time: string }) => {
      const [START_HOUR, START_MINUTES] = start_time.split(':').map(Number);
      const [END_HOUR, END_MINUTES] = end_time?.split(':')?.map(Number);

      const START_DATE = new Date();
      START_DATE.setHours(START_HOUR, START_MINUTES);

      const END_DATE = new Date();
      END_DATE.setHours(END_HOUR, END_MINUTES);

      return CURRENT_TIME >= START_DATE && (!END_DATE || CURRENT_TIME <= END_DATE);
   });
}

const message = computed(() => {
   const PERFORMED_HOURS = props.content.slot.performed_hours;
   const SHIFT_END_HOUR = Number(props.content.slot.ended_at.split(':')[1]);

   if (PERFORMED_HOURS[PERFORMED_HOURS.length - 1]?.end_time) {
      if (SHIFT_END_HOUR < new Date().getHours()) return t('till_tomorrow');
      return t('enjoy_your_break');
   }

   const INDEX_OF_ACTIVE_TIME_OBJECT = getIndexOfActiveTimeObject(props.content.slot.slot_hours);
   return t('do_not_forget_to_badge_out', { time: props.content.slot.slot_hours[INDEX_OF_ACTIVE_TIME_OBJECT]?.end_time });
});
// #endregion
</script>
<template>
   <div class="content-card">
      <div class="content-card__candidate">
         <InitialCircle :name="`${content.candidate.first_name} ${content.candidate.last_name}`" />
         <span class="content-card__candidate__name">{{ `${content.candidate.first_name} ${content.candidate.last_name}` }}</span>
      </div>
      <div class="content-card__squad">
         <span>{{ content.squad_name }}</span>
         <span v-for="(property, index) in content.properties" :key="index">&nbsp;{{ `- ${property.name}` }} </span>
      </div>

      <span class="content-card__date">{{ slot_date }}</span>
      <div class="content-card__slot-hours">
         <i class="content-card__slot-hours__icon fas fa-clock" />
         <div v-for="(slot_hour, index) in content.slot.slot_hours" :key="index" class="content-card__slot-hours__item">
            <span v-if="index > 0" class="content-card__slot-hours__item__divider">|</span>
            <span>{{ `${slot_hour.start_time} - ${slot_hour.end_time}` }}</span>
         </div>
         <span class="content-card__slot-hours__total">{{ slot_hours_total }}</span>
      </div>

      <div class="content-card__performed-hours">
         <div class="content-card__performed-hours__items">
            <div
               v-for="(performed_hour, index) in content.slot.performed_hours"
               :key="index"
               class="content-card__performed-hours__items__item"
            >
               <i :class="['content-card__performed-hours__items__item__icon', 'fas', `fa-${performed_hour.end_time ? 'stop' : 'play'}`]" />
               <span>{{ `${performed_hour.start_time} ${performed_hour.end_time ? '- ' + performed_hour.end_time : ''}` }}</span>
            </div>
         </div>
         <span class="content-card__performed-hours__total" :class="{ '-active': time_clock_in_progress }">{{ perfomed_hours_total }}</span>
      </div>

      <div class="content-card__message">
         <p>{{ message }}</p>
      </div>
   </div>
</template>
<style lang="scss" scoped>
.content-card {
   display: flex;
   flex-direction: column;
   gap: var(--nxt-gutter);
   width: 60%;

   padding: var(--nxt-gutter-large);

   font-size: var(--nxt-font-large);

   border-radius: var(--nxt-radius);
   background-color: var(--nxt-white);
}

.content-card__candidate {
   display: flex;
   flex-direction: row;
   align-items: center;
   gap: var(--nxt-gutter);

   font-size: calc(var(--nxt-font-large) * 2);
   font-weight: var(--nxt-font-bold);
}

.content-card__squad {
   display: flex;
   flex-direction: row;
}

.content-card__date {
   margin-bottom: var(--nxt-gutter-small);
   margin-bottom: var(--nxt-gutter-large);
}

.content-card__slot-hours {
   display: flex;
   flex-direction: row;
   align-items: center;
   margin-bottom: var(--nxt-gutter-large);

   &__icon {
      margin-right: var(--nxt-gutter-small);
   }
}

.content-card__slot-hours__item {
   display: flex;
   flex-direction: row;

   font-family: var(--nxt-font-family-monospace);

   &__divider {
      margin: 0 var(--nxt-gutter-small);
   }
}

.content-card__slot-hours__total,
.content-card__performed-hours__total {
   margin-left: auto;
   font-family: var(--nxt-font-family-monospace);
}

.content-card__performed-hours {
   display: flex;
   flex-direction: row;
   align-items: center;

   &__icon {
      margin-right: var(--nxt-gutter-small);
   }
}

.content-card__performed-hours__items {
   display: flex;
   flex-direction: column;
   gap: var(--nxt-gutter-small);
   font-family: var(--nxt-font-family-monospace);
}

.content-card__performed-hours__items__item {
   display: flex;
   flex-direction: row;
   align-items: center;

   &__icon {
      margin-right: var(--nxt-gutter);
   }
}

.content-card__performed-hours__total.-active {
   padding: var(--nxt-gutter-small);
   border-radius: var(--nxt-radius);
   background-color: var(--nxt-plan-show-blue);
   color: var(--nxt-white);
}

.content-card__message {
   display: flex;
   justify-content: center;
   margin: var(--nxt-gutter) 0;
}
</style>
